/**
 * Peças do quadro que vêm das fontes externas (E74.F2): o cluster de endereços
 * vinculados ao titular, com distância e faixa de proximidade, e a coordenada
 * da assinatura com a confiabilidade que ela sustenta.
 *
 * A nota de cautela do tutorial fica FIXA sob a tabela, não escondida em
 * tooltip: proximidade é corroboração, nunca prova de quem contratou, e quem
 * lê o quadro precisa ver isso junto com o número.
 */
import {
  Badge,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  CoordenadaAvaliadaData,
  EnderecoClusterData,
  FaixaDistancia,
  QualidadeCoordenada,
} from '@/lib/exame_eletronico';

import { Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.quadro;

const METROS_POR_KM = 1000;

/** Faixas próximas em verde, distantes em âmbar; "não medida" em neutro. */
const FAIXA_VARIANT: Record<FaixaDistancia, 'success' | 'info' | 'warning' | 'neutral'> = {
  ate_100m: 'success',
  ate_500m: 'success',
  ate_2km: 'info',
  ate_10km: 'warning',
  acima_10km: 'warning',
  indeterminada: 'neutral',
};

const QUALIDADE_VARIANT: Record<QualidadeCoordenada, 'success' | 'warning' | 'neutral'> = {
  confiavel: 'success',
  limitada: 'warning',
  ausente: 'neutral',
};

const FORMATO_DATA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : FORMATO_DATA.format(d);
}

/** Metros abaixo de 1 km, quilômetros acima — a régua de leitura do laudo. */
export function formatarDistancia(metros: number | null | undefined): string {
  if (metros === null || metros === undefined) return '—';
  if (metros < METROS_POR_KM) return `${Math.round(metros)} m`;
  return `${(metros / METROS_POR_KM).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
}

export function ClusterCadastral({
  cluster,
}: {
  cluster: readonly EnderecoClusterData[];
}): JSX.Element {
  return (
    <section data-testid="quadro-cluster">
      <Titulo>{t.cluster}</Titulo>
      {cluster.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.clusterVazio}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.colunaPessoa}</TableHead>
                  <TableHead>{t.colunaVinculo}</TableHead>
                  <TableHead>{t.colunaEndereco}</TableHead>
                  <TableHead>{t.colunaFonte}</TableHead>
                  <TableHead>{t.colunaDataFonte}</TableHead>
                  <TableHead>{t.colunaDistancia}</TableHead>
                  <TableHead>{t.colunaProximidade}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cluster.map((endereco, indice) => (
                  <TableRow
                    key={`${endereco.pessoa}-${endereco.endereco}-${indice}`}
                    data-testid={`quadro-cluster-${indice}`}
                    data-faixa={endereco.faixa}
                  >
                    <TableCell className="whitespace-nowrap">{endereco.pessoa}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {t.vinculoOpcoes[endereco.vinculo]}
                    </TableCell>
                    <TableCell className="max-w-[20rem]">{endereco.endereco}</TableCell>
                    <TableCell className="whitespace-nowrap text-fg-muted">
                      {endereco.fonte}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-fg-muted">
                      {formatarData(endereco.data_da_fonte)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <MonoData>{formatarDistancia(endereco.distancia_m)}</MonoData>
                    </TableCell>
                    <TableCell>
                      <Badge variant={FAIXA_VARIANT[endereco.faixa]}>
                        {t.faixaOpcoes[endereco.faixa]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-sc-2 text-caption text-fg-dim" data-testid="quadro-cluster-cautela">
            {t.clusterCautela}
          </p>
        </>
      )}
    </section>
  );
}

export function CoordenadaAvaliada({
  coordenada,
}: {
  coordenada: CoordenadaAvaliadaData;
}): JSX.Element {
  const fontes = ptBR.exameEletronico.form.fonteCoordenadaOpcoes;
  return (
    <div
      className="flex flex-wrap items-center gap-x-sc-6 gap-y-sc-2"
      data-testid="quadro-coordenada-detalhe"
      data-qualidade={coordenada.qualidade}
    >
      <MonoData>
        {coordenada.latitude.toFixed(6)}, {coordenada.longitude.toFixed(6)}
      </MonoData>
      <span className="text-body-sm text-fg-muted">
        {t.coordenadaFonte}: {fontes[coordenada.fonte]}
      </span>
      <span className="text-body-sm text-fg-muted">
        {t.coordenadaPrecisao}:{' '}
        {coordenada.precisao_m === null || coordenada.precisao_m === undefined
          ? t.coordenadaSemPrecisao
          : `${Math.round(coordenada.precisao_m)} m`}
      </span>
      <span className="flex items-center gap-sc-2 text-body-sm text-fg-muted">
        {t.coordenadaQualidade}
        <Badge variant={QUALIDADE_VARIANT[coordenada.qualidade]}>
          {t.qualidadeOpcoes[coordenada.qualidade]}
        </Badge>
      </span>
      {coordenada.qualidade === 'limitada' ? (
        <p className="w-full text-caption text-fg-dim" data-testid="quadro-coordenada-cautela">
          {t.qualidadeLimitadaHint}
        </p>
      ) : null}
    </div>
  );
}
